import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'

interface SignUpPayload {
  email: string
  password: string
  fullName?: string
  organizationName?: string
  position?: string
}

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signUp: (payload: SignUpPayload) => Promise<{ data: { user: User | null; session: Session | null } | null; error: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<{ error: AuthError | null }>
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async ({ email, password, fullName, organizationName, position }: SignUpPayload) => {
    try {
      const metadata: Record<string, string | undefined> = {
        full_name: fullName,
        organization_name: organizationName,
        position
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata
        },
      })

      if (data.user && !error) {
        console.log('User signed up successfully:', data.user.email)
        console.log('User ID:', data.user.id)
        console.log('Organization metadata stored:', organizationName)

        // Send welcome email
        try {
          await supabase.functions.invoke('send-welcome', {
            body: {
              email,
              fullName
            }
          })
          console.log('Welcome email sent')
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError)
          // Don't fail signup if email fails
        }
      }

      return { data, error }
    } catch (error) {
      return { data: null, error: error as AuthError }
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      return { error }
    } catch (error) {
      return { error: error as AuthError }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch (error) {
      return { error: error as AuthError }
    }
  }

  const resetPassword = async (email: string) => {
    try {
      // Use environment variable for platform URL, fallback to production URL
      const platformUrl = import.meta.env.VITE_PLATFORM_URL || 'https://helpdesk411.com'
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${platformUrl}/reset-password`,
      })
      return { error }
    } catch (error) {
      return { error: error as AuthError }
    }
  }

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    resetPassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
